import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * ColorPicker — swatch-grid color selector.
 *
 * Renders a trigger button showing the current color swatch. Clicking opens
 * a 4x2 grid of preset colors on the shared menu surface, portalled and
 * collision-aware so it is never clipped by a scrolling settings pane. The
 * active color shows a checkmark overlay.
 */

// Persisted project colours must be hex values (the API stores seven characters).
export const PRESET_COLORS = [
  "#30D158",
  "#5E5CE6",
  "#FF6B6B",
  "#FF9F0A",
  "#FFD60A",
  "#64D2FF",
  "#BF5AF2",
  "#FF375F",
];

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  colors?: string[];
  ariaLabel?: string;
  swatchLabel?: (color: string) => string;
  "data-testid"?: string;
}

export function ColorPicker({
  value,
  onChange,
  colors = PRESET_COLORS,
  ariaLabel,
  swatchLabel,
  "data-testid": testId = "color-picker",
}: ColorPickerProps) {
  const t = useT(uiMessages);
  return (
    <div className="inline-block" data-testid={testId}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-control-sm border border-glass-stroke transition-opacity hover:opacity-80"
            style={{ backgroundColor: value }}
            data-testid={`${testId}-trigger`}
            aria-label={ariaLabel ?? t("pickColor")}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          collisionPadding={8}
          className="min-w-0"
          data-testid={`${testId}-dropdown`}
        >
          <div className="grid w-[120px] grid-cols-4 gap-appkit-xs">
            {colors.map((color) => {
              const isActive = color === value;
              return (
                <DropdownMenuItem
                  key={color}
                  className={cn(
                    "h-6 min-h-0 w-6 justify-center rounded-control-sm p-0",
                    "transition-opacity hover:opacity-80",
                    "data-[highlighted]:ring-2 data-[highlighted]:ring-glass-stroke-light",
                    isActive &&
                      "ring-2 ring-glass-stroke-light ring-offset-1 ring-offset-transparent",
                  )}
                  style={{ backgroundColor: color }}
                  onSelect={() => onChange(color)}
                  data-testid={`${testId}-swatch-${color.replace(/[^a-zA-Z0-9]/g, "")}`}
                  aria-label={
                    swatchLabel
                      ? swatchLabel(color)
                      : t("colorSwatch", { color })
                  }
                  aria-current={isActive ? "true" : undefined}
                >
                  {isActive && (
                    <Icons.Check
                      className="h-3 w-3 drop-shadow-sm"
                      style={{ color: "var(--color-readable-overlay-primary)" }}
                    />
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export type { ColorPickerProps };
